import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileBarChart } from 'lucide-react';
import { Skeleton } from '../../../shared/components/Skeleton';
import { Alert } from '../../../shared/components/Alert';
import { TextInput } from '../../../shared/components/TextInput';
import { useIndicadoresContratos } from '../hooks';
import { formatCurrencyBRL } from '../utils/format';
import type { ContractSituacao } from '../api';

const SITUACAO_LABEL: Record<ContractSituacao, string> = {
  em_elaboracao: 'Em Elaboração',
  em_revisao_juridica: 'Em Rev. Jurídica',
  em_aprovacao: 'Em Aprovação',
  aguardando_assinatura: 'Aguard. Assinatura',
  assinado: 'Assinado',
  vigente: 'Vigente',
  suspenso: 'Suspenso',
  em_renovacao: 'Em Renovação',
  em_aditivo: 'Em Aditivo',
  encerrado: 'Encerrado',
  rescindido: 'Rescindido',
  cancelado: 'Cancelado',
};

interface KpiCardProps {
  label: string;
  value: React.ReactNode;
  sub?: string;
}

function KpiCard({ label, value, sub }: KpiCardProps) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-1">
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</p>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      {sub && <p className="text-xs text-gray-400">{sub}</p>}
    </div>
  );
}

export default function ContratosIndicadoresPage(): React.ReactElement {
  const navigate = useNavigate();

  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const yearAgo = useMemo(() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() - 1);
    return d.toISOString().slice(0, 10);
  }, []);

  const [periodFrom, setPeriodFrom] = useState(yearAgo);
  const [periodTo, setPeriodTo] = useState(today);

  const { data, isLoading, isError, error } = useIndicadoresContratos({ periodFrom, periodTo });

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto space-y-6">
      <div className="flex items-center gap-4">
        <button onClick={() => navigate('/contratos')} aria-label="Voltar" className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <FileBarChart className="w-5 h-5 text-blue-600" />
            Indicadores de Contratos
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-36"><TextInput label="De" type="date" value={periodFrom} onChange={(e) => setPeriodFrom(e.target.value)} /></div>
          <div className="w-36"><TextInput label="Até" type="date" value={periodTo} onChange={(e) => setPeriodTo(e.target.value)} /></div>
        </div>
      </div>

      {isError && (
        <Alert variant="error">{(error as Error)?.message ?? 'Erro ao carregar indicadores.'}</Alert>
      )}

      {isLoading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-28 rounded-xl" />)}
        </div>
      ) : data ? (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard
              label="Contratos Vigentes"
              value={data.contratosVigentesCount}
              sub={`${data.totalContratos} no total`}
            />
            <KpiCard
              label="Valor Total Contratado"
              value={formatCurrencyBRL(data.valorTotalContratado)}
            />
            <KpiCard
              label="Saldo Disponível Agregado"
              value={formatCurrencyBRL(data.saldoDisponivelAgregado)}
            />
            <KpiCard
              label="Taxa de Renovação"
              value={data.taxaRenovacao !== null ? `${data.taxaRenovacao.toFixed(1)}%` : '—'}
            />
          </div>

          {/* Tempo médio de assinatura */}
          {data.tempoMedioAssinatura !== null && (
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <p className="text-sm font-medium text-gray-700">Tempo Médio de Assinatura</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{data.tempoMedioAssinatura.toFixed(1)} dias</p>
            </div>
          )}

          {/* Próximos do vencimento */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h2 className="text-base font-semibold text-gray-900 mb-4">Contratos Próximos do Vencimento</h2>
            <div className="flex flex-wrap gap-3">
              {(
                [
                  { label: '7 dias', count: data.contratosProximosVencimento.dias7 },
                  { label: '15 dias', count: data.contratosProximosVencimento.dias15 },
                  { label: '30 dias', count: data.contratosProximosVencimento.dias30 },
                  { label: '60 dias', count: data.contratosProximosVencimento.dias60 },
                  { label: '90 dias', count: data.contratosProximosVencimento.dias90 },
                  { label: '180 dias', count: data.contratosProximosVencimento.dias180 },
                ] as const
              ).map(({ label, count }) => (
                <div key={label} className={`flex flex-col items-center px-4 py-3 rounded-lg border ${count > 0 ? 'border-orange-300 bg-orange-50' : 'border-gray-200 bg-gray-50'}`}>
                  <span className={`text-xl font-bold ${count > 0 ? 'text-orange-700' : 'text-gray-400'}`}>{count}</span>
                  <span className="text-xs text-gray-500 mt-0.5">{label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Totais por situação */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h2 className="text-base font-semibold text-gray-900 mb-4">Contratos por Situação</h2>
            <div className="space-y-2">
              {(Object.entries(data.totalPorSituacao) as [ContractSituacao, number][])
                .filter(([, count]) => count > 0)
                .sort(([, a], [, b]) => b - a)
                .map(([situacao, count]) => (
                  <div key={situacao} className="flex items-center gap-3">
                    <span className="text-sm text-gray-600 w-44 shrink-0">{SITUACAO_LABEL[situacao]}</span>
                    <div className="flex-1 bg-gray-100 rounded-full h-2 overflow-hidden">
                      <div
                        className="bg-blue-500 h-2 rounded-full"
                        style={{ width: `${data.totalContratos > 0 ? (count / data.totalContratos) * 100 : 0}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium text-gray-900 w-8 text-right">{count}</span>
                  </div>
                ))}
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
